import type { Text } from '@kkf/design'
import { appUrl } from '@kkf/design'
import { LINKS } from './links'

/**
 * The four things the Foundation is building, shown on the home page under "A Resort, A
 * Library, A Garage And An Partners" and again on the projects listing.
 *
 * These were four copies of one placeholder card — the same title, description and grey
 * image four times — which read as broken under that heading. Each now names what it
 * actually is and links to its own page. The listing repeats the first two to fill six
 * cards, as it always has.
 *
 * Three of the four leave the site entirely: the resort, the garage and the Partners are
 * their own apps on their own subdomains. This section is where the Foundation site does
 * its job as an entry point, so those three carry an absolute `href` rather than a route.
 */
/**
 * One card in the four-up rhythm, as DATA rather than as a card's props.
 *
 * It extended `ProjectCardProps` until 2026-09-18, which worked only while the two shapes
 * agreed — the props take plain strings and these carry both languages. `ProjectsPage`
 * maps one into the other.
 */
export interface ProjectEntry {
  image: string
  title: Text
  descr: Text
  to?: string
  href?: string
  /** How large the card draws — the design system's own prop, passed straight through. */
  size?: 'default' | 'sm'
  /** Bootstrap column classes for the 8/4/4/8 rhythm. */
  colClassName: string
}

const WIDE = 'col-lg-8 col-md-6 col-10 col-xxs-12'
const NARROW = 'col-lg-4 col-md-6 col-10 col-xxs-12'

const PLACES: ProjectEntry[] = [
  {
    image: '/assets/img/resort-guest-room.jpg',
    title: { en: 'The Resort', mn: 'Амралтын газар' },
    descr: { en: ' Rest, retreats & community', mn: ' Амралт, цугларалт, хамт олон' },
    href: LINKS.resort,
    size: 'default',
    colClassName: WIDE,
  },
  {
    image: '/assets/img/resort-terrace-reading.jpg',
    title: { en: 'The Library', mn: 'Номын сан' },
    descr: { en: ' Reading, study & mentoring', mn: ' Унших, суралцах, зөвлөгөө' },
    href: appUrl(LINKS, 'resort', '/library'),
    size: 'sm',
    colClassName: NARROW,
  },
  {
    image: '/assets/img/garage-trail-training.jpg',
    title: { en: 'King of Kings Garage', mn: 'King of Kings Garage' },
    descr: { en: ' Trades, robotics & mentoring', mn: ' Мэргэжил, робот техник, зөвлөгөө' },
    href: LINKS.garage,
    size: 'sm',
    colClassName: NARROW,
  },
  {
    image: '/assets/img/garage-off-road-fleet.jpg',
    title: { en: 'King of Kings Partners', mn: 'King of Kings Partners' },
    descr: { en: ' Christian business & services', mn: ' Христийн бизнес, үйлчилгээ' },
    href: LINKS.partners,
    size: 'default',
    colClassName: WIDE,
  },
]

/** Projects listing — 6 cards: the four places, then the first two again. */
export const PROJECTS: ProjectEntry[] = [...PLACES, ...PLACES.slice(0, 2)]

/** The home page shows the four places. */
export const PROJECTS_HOME = PLACES
